"use client";

import { useSession, signOut } from "next-auth/react";
import { useEffect } from "react";

/**
 * Versão mínima exigida do token.
 * Aumente este número quando fizer mudanças que requerem relogin.
 * 
 * Histórico de versões:
 * - 1: Versão original (com isFinancial)
 * - 2: Nova versão (com isOperationalSquad, sem super_admin)
 */
const REQUIRED_TOKEN_VERSION = 2;

/**
 * Hook que verifica a versão do token e força relogin se necessário.
 * Use este hook no layout principal ou no componente de proteção de rotas.
 * 
 * Exemplo:
 * ```tsx
 * function Layout({ children }) {
 *   useTokenVersionCheck();
 *   return <>{children}</>;
 * }
 * ```
 */
export function useTokenVersionCheck() {
  const { data: session, status } = useSession();

  useEffect(() => {
    // Só verifica quando estiver autenticado
    if (status !== "authenticated" || !session?.user) {
      return;
    }

    // Verifica a versão do token
    const tokenVersion = (session.user as any).tokenVersion || 1;

    if (tokenVersion < REQUIRED_TOKEN_VERSION) {
      console.warn(
        `[TokenVersion] Token version ${tokenVersion} is outdated. Required: ${REQUIRED_TOKEN_VERSION}. Forcing relogin.`
      );

      // Mostrar alerta antes de deslogar
      alert(
        "Sua sessão expirou devido a uma atualização do sistema.\n\n" +
        "Por favor, faça login novamente para continuar.\n\n" +
        "Obrigado pela compreensão!"
      );

      // Forçar logout e redirecionar para login
      signOut({ 
        callbackUrl: "/login?reason=token_expired",
        redirect: true 
      });
    }
  }, [session, status]);
}

/**
 * Verifica se o token atual é válido (versão compatível).
 * Use esta função para verificações manuais.
 */
export function isTokenVersionValid(tokenVersion: number | undefined): boolean {
  return (tokenVersion || 1) >= REQUIRED_TOKEN_VERSION;
}

/**
 * Retorna a versão mínima requerida do token.
 */
export function getRequiredTokenVersion(): number {
  return REQUIRED_TOKEN_VERSION;
}
